import React, { useState, useMemo } from 'react';
import {
  X, ForkKnife, Users, CheckSquare, Trash, ChatCircleDots,
  Printer, ArrowsClockwise, CheckCircle, SkipForward, Copy,
} from '@phosphor-icons/react';

/* ============ Default WhatsApp templates (admin can pick or override) ============= */
const WA_TEMPLATES = [
  {
    id: 'reminder',
    label: 'Friendly reminder',
    body: 'Hi {parent} 👋\n\nJust a quick reminder about {event_title} on {date}, {time}.\nVenue: {venue}\n\nWe’re excited to host {child} — see you soon! 🎉',
  },
  {
    id: 'rsvp_chase',
    label: 'Chase RSVP',
    body: 'Hi {parent}, hope you’re doing well! 💌\n\nWe’re finalising headcount for {event_title} on {date}. Could you please confirm whether {child} will be able to make it? RSVP link: {rsvp_link}\n\nThank you!',
  },
  {
    id: 'carpool',
    label: 'Carpool ask',
    body: 'Hi {parent}, would you be open to a carpool for {event_title} on {date}? Happy to coordinate pickup/drop with another family in your area 🚗\n\nLet me know if it helps!',
  },
  {
    id: 'bring_changes',
    label: 'Bring a change of clothes',
    body: 'Hi {parent}, just a heads up — {event_title} will have some splash/messy activities. Please pack a spare set of clothes for {child} along with their RSVP link: {rsvp_link}\n\nSee you on {date}! 🌊',
  },
  {
    id: 'thank_you',
    label: 'Thank you (post-event)',
    body: 'Thank you {parent} for joining {event_title} 💖\n\nIt meant a lot having {child} celebrate with us. We’ll share photos soon!',
  },
];

function fillTemplate(tpl, ctx) {
  if (!tpl) return '';
  return tpl
    .replaceAll('{parent}', ctx.parent || 'there')
    .replaceAll('{child}', ctx.child || 'your little one')
    .replaceAll('{event_title}', ctx.event_title || 'our event')
    .replaceAll('{date}', ctx.date || '')
    .replaceAll('{time}', ctx.time || '')
    .replaceAll('{venue}', ctx.venue || '')
    .replaceAll('{rsvp_link}', ctx.rsvp_link || '');
}

/* ============== Sticky bulk action bar (appears when 1+ selected) ============== */

export function BulkActionBar({
  selectedCount, totalCount, onClearSelection, onSelectAllVisible,
  onSetDietary, onAddAcc, onRemoveAcc, onSetAttendingNo, onDelete,
  onSendWhatsApp, onPrintLabels,
  dietaryOptions = ['Veg', 'Non-veg', 'Jain', 'Allergies'],
  accompanyingOptions = ['Mom', 'Dad', 'Nanny', 'Driver', 'Grandparent'],
}) {
  const [openMenu, setOpenMenu] = useState(null); // 'diet' | 'add_acc' | 'rem_acc' | null
  if (selectedCount === 0) return null;
  return (
    <div className="sticky top-2 z-30 mb-2" data-testid="bulk-action-bar">
      <div className="glass-card border border-[#FF6B35]/40 px-3 py-2 flex items-center gap-2 flex-wrap shadow-lg shadow-orange-500/10">
        <div className="flex items-center gap-2 text-xs font-bold text-white shrink-0">
          <CheckSquare size={14} weight="fill" className="text-[#FF6B35]" />
          <span data-testid="bulk-selected-count">{selectedCount}</span>
          <span className="text-neutral-500">of {totalCount}</span>
        </div>

        <button onClick={onSelectAllVisible} data-testid="bulk-select-all"
          className="text-[10px] font-bold text-neutral-400 hover:text-white px-1.5 py-1 rounded">
          Select all visible
        </button>

        <div className="h-4 w-px bg-white/10" />

        {/* Dietary */}
        <div className="relative">
          <button onClick={() => setOpenMenu(m => m === 'diet' ? null : 'diet')}
            data-testid="bulk-action-diet"
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 inline-flex items-center gap-1">
            <ForkKnife size={11} weight="bold" /> Set diet
          </button>
          {openMenu === 'diet' && (
            <Menu>
              {dietaryOptions.map(o => (
                <MenuItem key={o} onClick={() => { setOpenMenu(null); onSetDietary(o); }}
                  testid={`bulk-set-diet-${o.toLowerCase()}`}>{o}</MenuItem>
              ))}
            </Menu>
          )}
        </div>

        {/* Accompanying add */}
        <div className="relative">
          <button onClick={() => setOpenMenu(m => m === 'add_acc' ? null : 'add_acc')}
            data-testid="bulk-action-add-acc"
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 inline-flex items-center gap-1">
            <Users size={11} weight="bold" /> + Accompanying
          </button>
          {openMenu === 'add_acc' && (
            <Menu>
              {accompanyingOptions.map(o => (
                <MenuItem key={o} onClick={() => { setOpenMenu(null); onAddAcc(o); }}
                  testid={`bulk-add-acc-${o.toLowerCase()}`}>+ {o}</MenuItem>
              ))}
            </Menu>
          )}
        </div>

        {/* Accompanying remove */}
        <div className="relative">
          <button onClick={() => setOpenMenu(m => m === 'rem_acc' ? null : 'rem_acc')}
            data-testid="bulk-action-rem-acc"
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-neutral-500/15 text-neutral-300 border border-neutral-500/30 hover:bg-neutral-500/25 inline-flex items-center gap-1">
            <Users size={11} weight="bold" /> − Accompanying
          </button>
          {openMenu === 'rem_acc' && (
            <Menu>
              {accompanyingOptions.map(o => (
                <MenuItem key={o} onClick={() => { setOpenMenu(null); onRemoveAcc(o); }}
                  testid={`bulk-rem-acc-${o.toLowerCase()}`}>− {o}</MenuItem>
              ))}
            </Menu>
          )}
        </div>

        <button onClick={onSetAttendingNo} data-testid="bulk-action-mark-no"
          className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 inline-flex items-center gap-1">
          <X size={11} weight="bold" /> Mark No
        </button>

        <button onClick={onSendWhatsApp} data-testid="bulk-action-whatsapp"
          className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 inline-flex items-center gap-1">
          <ChatCircleDots size={11} weight="bold" /> WhatsApp
        </button>

        <button onClick={onPrintLabels} data-testid="bulk-action-print-labels"
          className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 inline-flex items-center gap-1">
          <Printer size={11} weight="bold" /> Labels
        </button>

        <button onClick={onDelete} data-testid="bulk-action-delete"
          className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25 inline-flex items-center gap-1">
          <Trash size={11} weight="bold" /> Delete
        </button>

        <div className="ml-auto" />
        <button onClick={onClearSelection} data-testid="bulk-clear"
          className="text-[10px] text-neutral-400 hover:text-white p-1">
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function Menu({ children }) {
  return (
    <div className="absolute z-40 mt-1 left-0 min-w-[140px] bg-neutral-900 border border-white/10 rounded-lg shadow-2xl py-1">
      {children}
    </div>
  );
}
function MenuItem({ onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full text-left px-3 py-1.5 text-[11px] font-semibold text-neutral-200 hover:bg-white/5">
      {children}
    </button>
  );
}

/* ============== Filter chip row (above the responses table) ============== */

export const RSVP_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'attending', label: 'Attending' },
  { id: 'not_attending', label: 'Not attending' },
  { id: 'has_nanny', label: 'Has nanny' },
  { id: 'veg', label: 'Veg' },
  { id: 'non_veg', label: 'Non-veg' },
  { id: 'jain', label: 'Jain' },
  { id: 'has_siblings', label: 'Has siblings' },
  { id: 'has_phone', label: 'Has phone' },
  { id: 'no_phone', label: 'Missing phone' },
];

export function applyRsvpFilter(rsvps, filterId, eventCustomFields = []) {
  if (!filterId || filterId === 'all') return rsvps;
  const sibFid = (eventCustomFields.find(cf => cf.field_type === 'siblings') || {}).field_id;
  return rsvps.filter(r => {
    const attending = 'yes' in {} ? false : (r.can_attend || '').toLowerCase().includes('yes');
    const hasNanny = (r.accompanying || []).some(a => /nanny/i.test(a));
    const diet = (r.dietary || '').toLowerCase();
    const hasSib = !!(sibFid && r.custom_responses?.[sibFid] &&
      ((r.custom_responses[sibFid].list || []).length > 0 ||
        (r.custom_responses[sibFid].attending || '').toLowerCase() === 'yes'));
    const hasPhone = !!(r.parent_phone && r.parent_phone.replace(/\D/g, '').length >= 6);
    switch (filterId) {
      case 'attending': return attending;
      case 'not_attending': return !attending;
      case 'has_nanny': return hasNanny;
      case 'veg': return diet === 'veg';
      case 'non_veg': return diet === 'non-veg' || diet === 'non veg' || diet === 'nonveg';
      case 'jain': return diet === 'jain';
      case 'has_siblings': return hasSib;
      case 'has_phone': return hasPhone;
      case 'no_phone': return !hasPhone;
      default: return true;
    }
  });
}

export function FilterChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="rsvp-filter-chips">
      {RSVP_FILTERS.map(f => (
        <button key={f.id} onClick={() => onChange(f.id)} data-testid={`filter-${f.id}`}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all ${value === f.id ? 'bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
          {f.label}
        </button>
      ))}
    </div>
  );
}

/* ============== WhatsApp queue modal — one chat at a time ============== */

export function WhatsAppQueueModal({ event, rsvps, onClose }) {
  const ctx0 = useMemo(() => ({
    event_title: event.title || '',
    date: event.event_date || '',
    time: event.event_time || '',
    venue: event.venue || '',
    rsvp_link: `${window.location.origin}/party/${event.slug || ''}`,
  }), [event]);

  const [tplId, setTplId] = useState(WA_TEMPLATES[0].id);
  const [customBody, setCustomBody] = useState(WA_TEMPLATES[0].body);
  const [useCustom, setUseCustom] = useState(false);
  const [idx, setIdx] = useState(0);
  const [sent, setSent] = useState({}); // {rsvp_id: 'sent' | 'skipped'}

  const eligible = useMemo(() =>
    rsvps.filter(r => r.parent_phone && r.parent_phone.replace(/\D/g, '').length >= 6),
    [rsvps]
  );

  const cur = eligible[idx];
  const tpl = useCustom ? customBody : (WA_TEMPLATES.find(t => t.id === tplId)?.body || '');
  const message = cur ? fillTemplate(tpl, {
    ...ctx0,
    parent: cur.parent_name || '',
    child: cur.child_name || '',
  }) : '';

  const sentCount = Object.values(sent).filter(v => v === 'sent').length;
  const skippedCount = Object.values(sent).filter(v => v === 'skipped').length;

  const handleSend = () => {
    if (!cur) return;
    const ph = (cur.parent_phone || '').replace(/[^\d+]/g, '').replace(/^\+/, '');
    window.open(`https://wa.me/${ph}?text=${encodeURIComponent(message)}`, '_blank');
    setSent(s => ({ ...s, [cur.rsvp_id]: 'sent' }));
    if (idx < eligible.length - 1) setIdx(i => i + 1);
  };
  const handleSkip = () => {
    if (!cur) return;
    setSent(s => ({ ...s, [cur.rsvp_id]: 'skipped' }));
    if (idx < eligible.length - 1) setIdx(i => i + 1);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
  };

  const noEligible = eligible.length === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="wa-queue-modal">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <ChatCircleDots size={14} weight="fill" className="text-emerald-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-white truncate">WhatsApp queue · {eligible.length} parent{eligible.length !== 1 ? 's' : ''}</h3>
              <p className="text-[10px] text-neutral-500">Sent {sentCount} · Skipped {skippedCount}{rsvps.length - eligible.length > 0 ? ` · ${rsvps.length - eligible.length} no phone` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="wa-queue-close"><X size={18} /></button>
        </div>

        {noEligible ? (
          <div className="p-10 text-center text-neutral-500 text-sm">
            None of the selected RSVPs have a phone number. Add phone numbers and try again.
          </div>
        ) : (
          <>
            {/* Template picker */}
            <div className="px-5 py-3 border-b border-white/10 space-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {WA_TEMPLATES.map(t => (
                  <button key={t.id}
                    onClick={() => { setTplId(t.id); setUseCustom(false); setCustomBody(t.body); }}
                    data-testid={`wa-tpl-${t.id}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${!useCustom && tplId === t.id ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                    {t.label}
                  </button>
                ))}
                <button onClick={() => setUseCustom(v => !v)}
                  data-testid="wa-tpl-custom"
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${useCustom ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                  Custom ✎
                </button>
              </div>
              {useCustom && (
                <textarea value={customBody} onChange={e => setCustomBody(e.target.value)}
                  data-testid="wa-custom-textarea"
                  className="w-full min-h-[90px] px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-xs text-neutral-200 outline-none resize-y" />
              )}
              <p className="text-[9px] text-neutral-500">Variables: <code className="text-neutral-300">{'{parent}'}</code> <code className="text-neutral-300">{'{child}'}</code> <code className="text-neutral-300">{'{event_title}'}</code> <code className="text-neutral-300">{'{date}'}</code> <code className="text-neutral-300">{'{time}'}</code> <code className="text-neutral-300">{'{venue}'}</code> <code className="text-neutral-300">{'{rsvp_link}'}</code></p>
            </div>

            {/* Current preview */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300 font-bold text-xs">
                    {(cur?.parent_name || cur?.child_name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-white truncate">{cur?.parent_name || cur?.child_name}</div>
                    <div className="text-[10px] text-neutral-500 tabular-nums">{cur?.parent_phone}</div>
                  </div>
                </div>
                <div className="text-[10px] text-neutral-500 tabular-nums">
                  {idx + 1} / {eligible.length}
                </div>
              </div>
              <div className="rounded-xl bg-emerald-500/[0.04] border border-emerald-500/20 p-3 whitespace-pre-wrap text-xs text-neutral-200 leading-relaxed font-mono"
                data-testid="wa-preview-body">
                {message || <span className="italic text-neutral-500">Empty message</span>}
              </div>
              <div className="flex items-center justify-between mt-3 gap-2">
                <button onClick={handleCopy} data-testid="wa-copy"
                  className="px-3 py-1.5 rounded-lg bg-white/5 text-neutral-300 text-[11px] font-semibold hover:bg-white/10 inline-flex items-center gap-1.5">
                  <Copy size={11} /> Copy text
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={handleSkip} disabled={!cur} data-testid="wa-skip"
                    className="px-3 py-1.5 rounded-lg bg-white/5 text-neutral-300 text-[11px] font-bold hover:bg-white/10 inline-flex items-center gap-1.5 disabled:opacity-40">
                    <SkipForward size={11} weight="bold" /> Skip
                  </button>
                  <button onClick={handleSend} disabled={!cur} data-testid="wa-send"
                    className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400 inline-flex items-center gap-1.5 disabled:opacity-50">
                    <ChatCircleDots size={12} weight="bold" /> Send &amp; next
                  </button>
                </div>
              </div>
            </div>

            {/* Mini queue list */}
            <div className="px-5 py-3 border-t border-white/10 max-h-[200px] overflow-y-auto">
              <div className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Queue</div>
              <div className="space-y-1">
                {eligible.map((r, i) => {
                  const st = sent[r.rsvp_id];
                  return (
                    <button key={r.rsvp_id} onClick={() => setIdx(i)}
                      data-testid={`wa-queue-row-${r.rsvp_id}`}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded text-left ${i === idx ? 'bg-emerald-500/10 border border-emerald-500/30' : 'hover:bg-white/5 border border-transparent'}`}>
                      <span className="text-[9px] font-bold text-neutral-500 tabular-nums w-5">#{i + 1}</span>
                      <span className="text-xs text-neutral-200 truncate flex-1">{r.parent_name || r.child_name}</span>
                      <span className="text-[10px] text-neutral-500 tabular-nums">{r.parent_phone}</span>
                      {st === 'sent' && <CheckCircle size={12} weight="fill" className="text-emerald-400 shrink-0" />}
                      {st === 'skipped' && <SkipForward size={12} weight="fill" className="text-neutral-500 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 text-right">
          <button onClick={onClose} data-testid="wa-queue-done"
            className="px-3 py-2 rounded-lg bg-white/5 text-neutral-200 text-xs font-semibold hover:bg-white/10">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
